/**
 * Subdomain Utility
 * Extracts tenant subdomain from current URL for multi-tenant routing
 */

/**
 * Extract subdomain from current hostname
 * @returns {string|null} - Subdomain or null if on main domain
 */
export const getSubdomain = () => {
  const hostname = window?.location?.hostname;
  
  // Handle localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }
  
  // Handle Rocket development URLs (e.g., crmpro1762237.builtwithrocket.new)
  // These should NOT be treated as tenant subdomains
  if (hostname?.includes('.builtwithrocket.new')) {
    return null;
  }
  
  // Split hostname into parts
  const parts = hostname?.split('.');
  
  // Main domain patterns:
  // localprocrm.com -> no subdomain
  // www.localprocrm.com -> no subdomain (www is ignored)
  // test.localprocrm.com -> subdomain: 'test'
  // company.localprocrm.com -> subdomain: 'company'
  
  if (parts?.length <= 2) {
    // No subdomain (e.g., localprocrm.com)
    return null;
  }
  
  // Get first part as subdomain
  const subdomain = parts?.[0];
  
  // Ignore 'www' as subdomain
  if (subdomain === 'www') {
    return null;
  }
  
  return subdomain;
};

/**
 * Check if current URL is on a tenant subdomain
 * @returns {boolean}
 */
export const isOnTenantSubdomain = () => {
  return getSubdomain() !== null;
};

/**
 * Get full tenant URL for a given subdomain
 * @param {string} subdomain - Tenant subdomain
 * @returns {string} - Full URL
 */
export const getTenantUrl = (subdomain) => {
  const protocol = window?.location?.protocol;
  const hostname = window?.location?.hostname;
  
  // Handle localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:${window?.location?.port || 3000}`;
  }
  
  // Get base domain (remove existing subdomain if present)
  const parts = hostname?.split('.');
  const baseDomain = parts?.length > 2 ? parts?.slice(-2)?.join('.') : hostname;
  
  return `${protocol}//${subdomain}.${baseDomain}`;
};

/**
 * Redirect to tenant subdomain
 * @param {string} subdomain - Target tenant subdomain
 */
export const redirectToTenant = (subdomain) => {
  const targetUrl = getTenantUrl(subdomain);
  window.location.href = targetUrl;
};

/**
 * Get main domain URL (without subdomain)
 * @returns {string}
 */
export const getMainDomainUrl = () => {
  const protocol = window?.location?.protocol;
  const hostname = window?.location?.hostname;
  
  // Handle localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:${window?.location?.port || 3000}`;
  }
  
  // Get base domain
  const parts = hostname?.split('.');
  const baseDomain = parts?.length > 2 ? parts?.slice(-2)?.join('.') : hostname;
  
  return `${protocol}//${baseDomain}`;
};